import { motion } from 'framer-motion';
import { Play, Home, Volume2, VolumeX, Pause } from 'lucide-react';
interface PauseOverlayProps {
  onResume: () => void;
  onQuit: () => void;
  muted: boolean;
  onToggleMute: () => void;
  score: number;
  highScore: number;
}
export function PauseOverlay({
  onResume,
  onQuit,
  muted,
  onToggleMute,
  score,
  highScore,
}: PauseOverlayProps) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.25 }}
      className="absolute inset-0 z-30 bg-black/70 backdrop-blur-md flex items-center justify-center px-6"
    >
      <motion.div
        initial={{ scale: 0.85, opacity: 0, y: 30 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.85, opacity: 0, y: 30 }}
        transition={{ type: 'spring', damping: 25, stiffness: 300, delay: 0.1 }}
        className="w-full max-w-sm bg-gradient-to-b from-amber-50 to-orange-100 rounded-3xl p-7 shadow-2xl border-4 border-amber-200/80"
      >
        <motion.div
          initial={{ rotate: -20, scale: 0.5 }}
          animate={{ rotate: 0, scale: 1 }}
          transition={{ type: 'spring', damping: 15, stiffness: 200, delay: 0.2 }}
          className="flex items-center justify-center mb-3"
        >
          <div className="bg-amber-500 rounded-full p-3 shadow-lg">
            <Pause className="w-8 h-8 text-white" strokeWidth={2.5} />
          </div>
        </motion.div>
        <h2 className="text-4xl font-black text-amber-900 text-center mb-1 tracking-tight">
          Paused
        </h2>
        <p className="text-amber-700 text-center text-sm font-semibold mb-5">
          Take a breath, rider
        </p>
        <div className="bg-white/70 rounded-2xl px-4 py-3 mb-5 flex justify-between text-amber-900">
          <div className="text-center flex-1">
            <div className="text-[10px] font-bold uppercase tracking-widest text-amber-700">
              Score
            </div>
            <div className="text-2xl font-black tabular-nums">{score.toLocaleString()}</div>
          </div>
          <div className="w-px bg-amber-200" />
          <div className="text-center flex-1">
            <div className="text-[10px] font-bold uppercase tracking-widest text-amber-700">
              Best
            </div>
            <div className="text-2xl font-black tabular-nums text-amber-600">
              {highScore.toLocaleString()}
            </div>
          </div>
        </div>
        <div className="flex flex-col gap-3">
          <motion.button
            whileTap={{ scale: 0.95 }}
            whileHover={{ scale: 1.02 }}
            onClick={onResume}
            className="bg-amber-500 active:bg-amber-600 text-white font-black text-lg py-4 rounded-2xl shadow-lg flex items-center justify-center gap-2 min-h-[56px] transition-colors"
          >
            <Play className="w-6 h-6" strokeWidth={2.5} fill="currentColor" />
            Resume Ride
          </motion.button>
          <motion.button
            whileTap={{ scale: 0.95 }}
            whileHover={{ scale: 1.02 }}
            onClick={onToggleMute}
            className="bg-amber-800 active:bg-amber-900 text-white font-bold text-base py-3 rounded-2xl shadow flex items-center justify-center gap-2 min-h-[48px] transition-colors"
            aria-label={muted ? 'Unmute sound' : 'Mute sound'}
          >
            {muted ? (
              <VolumeX className="w-5 h-5" strokeWidth={2.5} />
            ) : (
              <Volume2 className="w-5 h-5" strokeWidth={2.5} />
            )}
            {muted ? 'Sound Off' : 'Sound On'}
          </motion.button>
          <motion.button
            whileTap={{ scale: 0.95 }}
            whileHover={{ scale: 1.02 }}
            onClick={onQuit}
            className="bg-stone-700 active:bg-stone-800 text-white font-bold text-base py-3 rounded-2xl shadow flex items-center justify-center gap-2 min-h-[48px] transition-colors"
          >
            <Home className="w-5 h-5" strokeWidth={2.5} />
            Quit to Lobby
          </motion.button>
        </div>
      </motion.div>
    </motion.div>
  );
}